import qs from 'qs';
import { makeStyles } from '@material-ui/core/styles';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import type { Entity } from '@backstage/catalog-model';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { scmIntegrationsApiRef } from '@backstage/integration-react';
import {
  getEntitySourceLocation,
  useStarredEntity,
} from '@backstage/plugin-catalog-react';

/*
 * The entity header's action group: Star and Repo as secondary buttons
 * (white, hairline ring, radius 8, 38px), plus the `metadata.links`-driven
 * CTA in the charcoal primary style. "View relations" used to live here too,
 * but it's now a header link on the Relations rail card instead — colocated
 * with the relationship data it opens a graph of, and leaving this row free
 * for future high-priority buttons rather than crowding it with a "go look
 * at more" action unrelated to Star/Repo's identity/source pairing.
 *
 * Every button is driven by data the entity actually holds. The source link
 * comes from `getEntitySourceLocation`, the CTA from `metadata.links`. An
 * entity with neither gets Star alone — the group never renders a dead
 * button and never invents a destination.
 *
 * Measured from the mock:
 *   secondary  height 38; padding 0 14px; radius 8; #fff;
 *              box-shadow 0 0 0 1px rgba(0,0,0,0.15); 13px/500 #202020
 *   primary    height 38; padding 0 16px; radius 8; #202020; #fefeec;
 *              trailing chevron in brand yellow
 */

/*
 * Primary calls to action, matched against `metadata.links` by icon or title.
 * First match wins; no match means no primary button.
 */
const PRIMARY_ACTIONS: ReadonlyArray<{ pattern: RegExp; label: string }> = [
  { pattern: /runbook/i, label: 'Open runbook' },
  { pattern: /dashboard/i, label: 'Open dashboard' },
];

/*
 * Kinds where a source link means a repository. A Group's own source location
 * is the `githubOrg` provider's, which resolves to
 * `https://github.com/orgs/<org>/teams/` — an org listing with no team slug, so
 * the button would be labelled wrong and go nowhere useful. Those kinds get
 * Star alone until there is a correct team URL to link under a correct label.
 */
const REPO_LINK_KINDS = new Set([
  'repository',
  'component',
  'api',
  'resource',
  'system',
  'domain',
]);

/** The repo URL for the header button, or nothing if the entity has none. */
export function repoUrl(
  entity: Entity,
  location: { locationTargetUrl?: string } | undefined,
): string | undefined {
  if (!REPO_LINK_KINDS.has(entity.kind.toLocaleLowerCase('en-US'))) {
    return undefined;
  }
  const url = location?.locationTargetUrl;
  if (!url) {
    return undefined;
  }
  // A bare host, or a path that ends where it began, is not a repository.
  try {
    const { pathname } = new URL(url);
    return pathname.replace(/^\/|\/$/g, '') ? url : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The Catalog Graph page (`/catalog-graph`) draws nothing on its own — it
 * only renders relationships for the `rootEntityRefs` it's given, and has no
 * entity picker of its own. This is the one way in: deep-link it with the
 * entity the button was clicked from.
 *
 * The vendor's own defaults (every kind, every relation, infinite depth) turn
 * that into a graph of the entire catalog reachable from this one node — the
 * page's Filters panel is still there to widen it, but the button should open
 * on just this entity's direct neighbors, not everything transitively
 * connected to it.
 */
export function catalogGraphUrl(entity: Entity): string {
  return `/catalog-graph?${qs.stringify(
    { rootEntityRefs: [stringifyEntityRef(entity)], maxDepth: 1 },
    { arrayFormat: 'brackets' },
  )}`;
}

export function primaryAction(entity: Entity) {
  for (const { pattern, label } of PRIMARY_ACTIONS) {
    const link = entity.metadata.links?.find(
      l => pattern.test(l.icon ?? '') || pattern.test(l.title ?? ''),
    );
    if (link) {
      return { label, href: link.url };
    }
  }
  return undefined;
}

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 38,
    padding: '0 14px',
    borderRadius: 8,
    background: 'var(--bui-white)',
    boxShadow: 'var(--hone-portal-ring-control)',
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1,
    color: 'var(--bui-fg-primary)',
    textDecoration: 'none',
    border: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': {
      textDecoration: 'none',
      color: 'var(--bui-fg-primary)',
      background: 'var(--bui-bg-neutral-2)',
    },
  },
  starIcon: {
    fontSize: 15,
    color: 'var(--bui-fg-secondary)',
  },
  starred: {
    color: 'var(--bui-fg-primary)',
  },
  externalGlyph: {
    fontSize: 11,
    color: 'var(--bui-fg-secondary)',
  },
  primary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    height: 38,
    padding: '0 16px',
    borderRadius: 8,
    background: 'var(--hone-portal-surface-solid)',
    color: 'var(--helix-colors-brand-50)',
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      textDecoration: 'none',
      color: 'var(--helix-colors-brand-50)',
      background: 'var(--hone-portal-surface-solid-hover)',
    },
  },
  primaryChevron: {
    color: 'var(--helix-colors-brand-500)',
  },
});

export function EntityHeaderActions({ entity }: { entity: Entity }) {
  const classes = useStyles();
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const { isStarredEntity, toggleStarredEntity } = useStarredEntity(entity);

  const repo = repoUrl(entity, getEntitySourceLocation(entity, scmIntegrationsApi));
  const primary = primaryAction(entity);

  return (
    <div className={classes.root}>
      <button
        type="button"
        className={classes.button}
        onClick={toggleStarredEntity}
        aria-pressed={isStarredEntity}
      >
        {isStarredEntity ? (
          <StarIcon className={`${classes.starIcon} ${classes.starred}`} />
        ) : (
          <StarBorderIcon className={classes.starIcon} />
        )}
        {isStarredEntity ? 'Starred' : 'Star'}
      </button>

      {repo && (
        <Link
          className={classes.button}
          to={repo}
          title="View the source for this entity"
        >
          Repo
          <span className={classes.externalGlyph}>&#8599;</span>
        </Link>
      )}

      {primary && (
        <Link className={classes.primary} to={primary.href}>
          {primary.label}
          <span className={classes.primaryChevron}>&#8250;</span>
        </Link>
      )}
    </div>
  );
}
